/**
 * `<FormSheet>` — the right-side counterpart to `<FormDialog>`, same slot API.
 *
 * Create and edit flows use this; destructive confirmations stay on
 * `<FormDialog>`, where a centred modal is the point: it interrupts, and the
 * decision is one irreversible click rather than a form to work through.
 *
 * The slots are the same components `FormDialog` uses, so `FormSheet.Body` and
 * `FormDialog.Body` are one identity and swapping the shell in a consumer is a
 * change of chrome only — no body or footer rewrite, and no second copy of the
 * footer's loading-lock contract to keep in step.
 */
import { twMerge } from 'tailwind-merge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@monksflow/monks-ui';
import { FormShellBody, FormShellFooter } from '@/shared/ui/FormDialog/formShell';
import {
  FORM_SHELL_BODY_INSET,
  FormShellBodyInsetContext,
} from '@/shared/ui/FormDialog/formShell.context';
import { findSlots, guardNotistackDismissal } from '@/shared/ui/FormDialog/formShell.utils';
import type { FormSheetProps } from './FormSheet.types';

const FormSheetRoot = ({
  open,
  onOpenChange,
  title,
  description,
  children,
  className,
  dividers = false,
  toolbar,
  headerBar,
  'data-testid': dataTestId,
}: FormSheetProps) => {
  const { body, footer } = findSlots(children);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid={dataTestId}
        {...(description ? {} : { 'aria-describedby': undefined })}
        // twMerge, not template interpolation: a consumer widening the sheet
        // passes its own `w-*`, and both classes would otherwise survive with
        // CSS source order picking the winner.
        // The panel has no horizontal padding: each row carries `px-xl` itself, so the body can
        // span the panel with its inset inside its clip, and a rule on the header or footer
        // already runs edge to edge. The body adds `2xs` of ring room above and below itself,
        // so each gap is one step short of what it reads as, and so is the bottom padding when
        // the body is the last row.
        className={twMerge(
          'w-[30rem] gap-lg rounded-l-lg px-0 py-xl sm:max-w-none',
          !footer && 'pb-lg',
          // Each region owns the space around its own rule, so the gap between them closes to
          // the step the rule sits on - a wider gap would put more on one side of it than the
          // other and read as two different separations.
          dividers && 'gap-sm',
          className,
        )}
        onPointerDownOutside={guardNotistackDismissal}
        showCloseButton={headerBar === undefined}
      >
        <SheetHeader
          className={twMerge('p-0 px-xl', dividers && 'border-b border-border-light pb-md')}
        >
          {headerBar !== undefined && headerBar}
          <SheetTitle className={headerBar !== undefined ? 'sr-only' : undefined}>
            {title}
          </SheetTitle>
          {description ? <SheetDescription>{description}</SheetDescription> : null}
          {/* Inside the header, so the rule falls below it: what it holds acts on the body and
              belongs on this side of the line, and the header is already pinned by construction -
              no sticky needed in the scroll box. */}
          {toolbar !== undefined && <div className="pt-md">{toolbar}</div>}
        </SheetHeader>
        {/* The body owns the scroll so the header and footer stay pinned - a
            sheet is tall and narrow, and a panel-level scroll would carry them
            off screen. */}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <FormShellBodyInsetContext.Provider value={FORM_SHELL_BODY_INSET}>
            {body}
          </FormShellBodyInsetContext.Provider>
        </div>
        {footer && (
          <SheetFooter
            className={twMerge(
              'flex-row items-center justify-end gap-2 p-0 px-xl',
              dividers && 'border-t border-border-light pt-md',
            )}
          >
            {footer}
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
};

export const FormSheet = Object.assign(FormSheetRoot, {
  Body: FormShellBody,
  Footer: FormShellFooter,
});
